// src/components/LanguageSwitcher/LanguageSwitcher.jsx
import React, { useState } from 'react';
import { 
  Select, 
  MenuItem, 
  Button,
  Menu,
  ListItemIcon,
  ListItemText,
} from '@mui/material';
import { ChevronDown } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { useTheme } from '../../context/ThemeContext';

const languages = [
  { code: 'fi', flag: '🇫🇮' },
  { code: 'en', flag: '🇬🇧' },
];

export const LanguageSwitcher = ({ fullWidth = false }) => {
  const { t, i18n } = useTranslation();
  const { isDark } = useTheme();

  const [anchorEl, setAnchorEl] = useState(null);

  const currentLanguage = languages.find(l => l.code === i18n.language) || languages[0];
  const getLanguageLabel = (code) => t(`language.${code}`);

  const handleOpen = (event) => setAnchorEl(event.currentTarget);
  const handleClose = () => setAnchorEl(null);

  const handleChange = (code) => {
    i18n.changeLanguage(code);
    handleClose();
  };

  // Full width version for mobile drawer
  if (fullWidth) {
    return (
      <Select
        value={i18n.language}
        onChange={(e) => i18n.changeLanguage(e.target.value)}
        size="small"
        fullWidth
        sx={{
          fontFamily: 'Inter, sans-serif',
          '& .MuiSelect-select': {
            display: 'flex',
            alignItems: 'center',
            gap: 1,
          },
        }}
      >
        {languages.map((lang) => (
          <MenuItem 
            key={lang.code} 
            value={lang.code}
            sx={{ fontFamily: 'Inter, sans-serif' }}
          >
            <span style={{ marginRight: 8 }}>{lang.flag}</span>
            {getLanguageLabel(lang.code)}
          </MenuItem>
        ))}
      </Select>
    );
  }

  // Compact button version for header
  return (
    <>
      <Button
        onClick={handleOpen}
        size="small"
        sx={{
          color: 'inherit',
          textTransform: 'none',
          fontFamily: 'Inter, sans-serif',
          fontWeight: 500,
          minWidth: 'auto',
          px: 1.5,
          py: 0.75,
          borderRadius: 2,
          gap: 0.5,
          '&:hover': {
            bgcolor: isDark 
              ? 'rgba(255, 255, 255, 0.1)' 
              : 'rgba(255, 255, 255, 0.2)',
          },
        }}
      >
        <span style={{ fontSize: '1.1rem' }}>{currentLanguage.flag}</span>
        <ChevronDown size={16} />
      </Button>

      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleClose}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
        PaperProps={{
          sx: {
            borderRadius: 2,
            boxShadow: isDark 
              ? '0 4px 20px rgba(0, 0, 0, 0.3)' 
              : '0 4px 20px rgba(0, 0, 0, 0.1)',
            mt: 1,
            minWidth: 140,
          }
        }}
      >
        {languages.map((lang) => (
          <MenuItem
            key={lang.code}
            onClick={() => handleChange(lang.code)}
            selected={lang.code === i18n.language}
            sx={{
              fontFamily: 'Inter, sans-serif',
              fontWeight: 500,
              py: 1.25,
              '&.Mui-selected': {
                bgcolor: isDark ? 'rgba(72, 202, 228, 0.15)' : 'primary.light',
              },
              '&:hover': {
                bgcolor: isDark ? 'rgba(72, 202, 228, 0.1)' : 'primary.light',
              },
            }}
          >
            <ListItemIcon sx={{ minWidth: 32 }}>
              <span style={{ fontSize: '1.1rem' }}>{lang.flag}</span>
            </ListItemIcon>
            <ListItemText 
              primary={getLanguageLabel(lang.code)}
              primaryTypographyProps={{
                fontFamily: 'Inter, sans-serif',
                fontWeight: 500,
              }}
            />
          </MenuItem>
        ))}
      </Menu>
    </>
  );
};
